import React, { useEffect, useState, createElement } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Star, Clock, ShieldCheck, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Gift, Zap, Award, TrendingUp, Smile, DollarSign, AlertCircle, XCircle, HelpCircle } from 'lucide-react';
// Pixel ID real de Meta Events Manager para esta cuenta.
const META_PIXEL_ID = '1285677670304642';
function useMetaPixel(pixelId: string) {
  useEffect(() => {
    if (typeof window === 'undefined' || !pixelId) return;
    try {
      const w = window as any;
      const d = document;
      // Snippet oficial de Meta Pixel (base code).
      if (!w.fbq) {
        /* eslint-disable */
        ;
        (function (f: any, b: Document, e: string, v: string) {
          if (f.fbq) return;
          const n: any = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = '2.0';
          n.queue = [];
          const t = b.createElement(e) as HTMLScriptElement;
          t.async = true;
          t.src = v;
          const s = b.getElementsByTagName(e)[0];
          s.parentNode?.insertBefore(t, s);
        })(w, d, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
        /* eslint-enable */
      }
      w.fbq('init', pixelId);
      w.fbq('track', 'PageView');
      // Log de diagnóstico: confirma en la consola del navegador que el pixel arrancó.
      console.info('[Meta Pixel] init + PageView disparados. ID:', pixelId);
      // Fallback <noscript> estándar que Meta recomienda instalar en el <body>.
      // Ayuda a que el Pixel Helper y los rastreadores detecten el pixel.
      if (!d.getElementById('mp-fb-pixel-noscript')) {
        const noscript = d.createElement('noscript');
        noscript.id = 'mp-fb-pixel-noscript';
        const img = d.createElement('img');
        img.height = 1;
        img.width = 1;
        img.style.display = 'none';
        img.alt = '';
        img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
        noscript.appendChild(img);
        d.body.appendChild(noscript);
      }
    } catch (err) {
      // El pixel nunca debe romper el render de la página.
      console.warn('Meta Pixel no se pudo inicializar:', err);
    }
  }, [pixelId]);
}
export function App() {
  useMetaPixel(META_PIXEL_ID);
  return <div className="min-h-screen bg-[#FFFDF9] text-[#2D1816] font-['Inter',sans-serif] selection:bg-pink-200 selection:text-pink-900" data-id="element-0">
      <HeroSection data-id="element-1" />
      <PainSection data-id="element-2" />
      <SolutionSection data-id="element-3" />
      <TomosSection data-id="element-4" />
      <PeekInsideSection data-id="element-5" />
      <BonosSection data-id="element-6" />
      <ChangesSection data-id="element-7" />
      <OfferSection data-id="element-8" />
      <GuaranteeSection data-id="element-9" />
      <TestimonialsSection data-id="element-10" />
      <FAQSection data-id="element-11" />
      <FinalCloseSection data-id="element-12" />
      <Footer data-id="element-13" />
    </div>;
}
const CHECKOUT_URL = 'https://pay.hotmart.com/M106649735B?checkoutMode=10&utm_source=facebook&utm_medium=paid&utm_campaign=minidonas360';
// Dispara un evento del Meta Pixel de forma segura (nunca rompe el render).
function trackFbEvent(event: string, params?: Record<string, unknown>) {
  try {
    const w = window as any;
    if (w && typeof w.fbq === 'function') {
      w.fbq('track', event, params);
    }
  } catch (err) {
    console.warn('No se pudo registrar el evento del pixel:', err);
  }
}
const CTAButton = ({
  text,
  subtext
}: {
  text: string;
  subtext?: string;
}) => <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" onClick={() => trackFbEvent('InitiateCheckout', {
  content_name: 'Mega Pack Emprende Minidonas 360',
  currency: 'USD',
  value: 9.97
})} className="group relative inline-flex flex-col items-center justify-center w-full sm:w-auto px-8 py-4 text-lg font-bold text-white transition-all duration-200 bg-[#E85D75] border border-transparent rounded-xl hover:bg-[#D64A62] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E85D75] shadow-[0_0_20px_rgba(232,93,117,0.4)] hover:shadow-[0_0_30px_rgba(232,93,117,0.6)] hover:-translate-y-1" data-id="element-14">
    <span className="flex items-center gap-2 font-['Outfit',sans-serif] tracking-wide" data-id="element-15">
      {text}
    </span>
    {subtext && <span className="text-sm font-normal text-pink-100 mt-1" data-id="element-16">{subtext}</span>}
  </a>;
function HeroSection() {
  return <section className="relative pt-8 pb-16 md:pt-12 md:pb-24 px-4 overflow-hidden" data-id="element-17">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pink-100/60 via-[#FFFDF9] to-[#FFFDF9]" data-id="element-18"></div>
      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-19">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-pink-200 bg-pink-50 text-[#E85D75] text-sm font-bold tracking-wide mb-8 uppercase shadow-sm" data-id="element-20">
          <span className="w-2 h-2 rounded-full bg-[#E85D75] animate-pulse" data-id="element-21"></span>
          SISTEMA DE NEGOCIO COMPROBADO
        </div>

        <h1 className="md:text-6xl lg:text-7xl font-black font-['Outfit',sans-serif] tracking-tight mb-8 text-[#2D1816] text-[30px]" data-id="element-22">
          DEJA DE BUSCAR RECETAS SUELTAS EN INTERNET. AQUÍ TIENES EL{' '}
          <span className="text-[#E85D75]" data-id="element-23">SISTEMA COMPLETO DE 6 TOMOS</span>{' '}
          QUE CONVIERTE TU COCINA EN UN{' '}
          <span className="text-[#E85D75]" data-id="element-24">NEGOCIO RENTABLE DE MINIDONAS</span>{' '}
          DESDE EL PRIMER DÍA.
        </h1>

        <p className="md:text-2xl text-[#5C3D3A] mb-10 max-w-3xl mx-auto font-medium text-[18px]" data-id="element-25">
          Ahorrá tiempo, eliminá la confusión y empezá a vender minidonas
          profesionales — sin cursos caros, sin experiencia previa y sin salir
          de tu casa.
        </p>

        <div className="mb-10 max-w-4xl mx-auto" data-id="element-26">
          <img src="https://cdn.magicpatterns.com/uploads/7FByqGit8cRrPhzxMeXwZk/ChatGPT_Image_7_jul_2026,_06_46_52_p.m..png" alt="Mega Pack Emprende Minidonas 360 — 6 tomos completos más 3 bonos exclusivos" className="w-full h-auto rounded-2xl shadow-2xl border border-pink-100" data-id="element-27" />
        </div>

        <div className="mb-10" data-id="element-28">
          <p className="text-lg font-bold text-[#E85D75] mb-4 font-['Outfit',sans-serif]" data-id="element-29">
            Mega Pack Emprende Minidonas 360 — 6 Tomos + 3 Bonos
          </p>
          <CTAButton text="✅ SÍ, QUIERO MI PACK AHORA" data-id="element-30" />
        </div>

        <div className="flex flex-col items-center justify-center gap-3 text-sm text-[#5C3D3A] font-medium" data-id="element-31">
          <div className="flex items-center gap-1 text-amber-400" data-id="element-32">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-33" />)}
          </div>
          <p data-id="element-34">
            4.9/5 — Reseñas verificadas | Con la confianza de emprendedoras en
            toda Latinoamérica
          </p>
          <p className="flex items-center gap-1" data-id="element-35">
            <ShieldCheck className="w-4 h-4 text-green-500" data-id="element-36" /> Garantía de
            devolución de 7 días: riesgo cero.
          </p>
        </div>
      </div>
    </section>;
}
function PainSection() {
  const pains = [{
    emoji: '😰',
    title: 'Recetas sueltas que no funcionan',
    desc: 'Buscás en YouTube, en grupos de Facebook, en Pinterest... encontrás mil recetas distintas y ninguna te da el resultado que ves en las fotos.'
  }, {
    emoji: '🤷‍♀️',
    title: 'No sabés por dónde empezar',
    desc: '¿Primero las recetas? ¿Primero el packaging? ¿Primero Instagram? La falta de orden te paraliza antes de empezar.'
  }, {
    emoji: '📱',
    title: 'Sobrecarga de información contradictoria',
    desc: 'Cada influencer enseña algo diferente. Una dice una cosa, otra dice lo contrario. No sabés en qué sistema confiar ni qué funciona de verdad.'
  }, {
    emoji: '💸',
    title: 'Miedo a invertir y no recuperarlo',
    desc: 'Ya gastaste en ingredientes, en moldes, en cursos que prometían mucho y entregaron poco. No querés cometer el mismo error otra vez.'
  }];
  return <section className="py-20 px-4 bg-[#FFF5EB]" data-id="element-37">
      <div className="max-w-5xl mx-auto" data-id="element-38">
        <div className="text-center mb-16" data-id="element-39">
          <h2 className="text-3xl md:text-5xl font-black mb-4 text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-40">
            ¿TE SUENA FAMILIAR?
          </h2>
          <p className="text-xl text-[#5C3D3A] font-medium" data-id="element-41">
            Te encanta la idea de emprender con minidonas, pero algo siempre te
            frena.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-42">
          {pains.map((pain, idx) => <div key={idx} className="bg-white p-8 rounded-2xl border border-amber-100 hover:border-pink-300 hover:shadow-lg transition-all duration-300" data-id="element-43">
              <div className="mb-4 bg-pink-50 w-16 h-16 rounded-full flex items-center justify-center text-3xl" data-id="element-44">
                <span role="img" aria-label={pain.title} data-id="element-45">
                  {pain.emoji}
                </span>
              </div>
              <h3 className="text-xl font-bold mb-3 text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-46">
                {pain.title}
              </h3>
              <p className="text-[#5C3D3A] leading-relaxed" data-id="element-47">{pain.desc}</p>
            </div>)}
        </div>
      </div>
    </section>;
}
function SolutionSection() {
  return <section className="py-20 px-4 bg-[#FFFDF9]" data-id="element-48">
      <div className="max-w-4xl mx-auto text-center" data-id="element-49">
        <h2 className="text-3xl md:text-5xl font-black mb-6 text-[#E85D75] font-['Outfit',sans-serif]" data-id="element-50">
          LA SOLUCIÓN QUE ESTABAS ESPERANDO
        </h2>
        <h3 className="text-2xl md:text-3xl font-bold mb-8 text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-51">
          UN SISTEMA COMPLETO DE 6 TOMOS PARA EMPRENDER CON MINIDONAS
        </h3>
        <p className="text-xl text-[#5C3D3A] mb-10 font-medium" data-id="element-52">
          Diseñado específicamente para emprendedoras latinoamericanas que
          quieren resultados reales desde el primer día.
        </p>

        <div className="mb-10 rounded-2xl overflow-hidden shadow-xl border border-pink-100" data-id="element-53">
          <img src="https://cdn.magicpatterns.com/uploads/rzNQoAkqWcJW8WrroFxxhX/ChatGPT_Image_7_jul_2026,_12_47_04_a.m..png" alt="Los 6 tomos del Mega Pack Emprende Minidonas 360: un sistema completo para crear, vender y escalar tu negocio de minidonas" className="w-full h-auto block" data-id="element-54" />
        </div>

        <div className="bg-gradient-to-b from-[#4A2B29] to-[#3E201D] p-8 md:p-12 rounded-3xl border border-[#5C3D3A] shadow-2xl text-left relative overflow-hidden" data-id="element-55">
          <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl -mr-20 -mt-20" data-id="element-56"></div>

          <p className="text-lg text-pink-50 mb-6 leading-relaxed relative z-10" data-id="element-57">
            El{' '}
            <strong className="text-white font-bold" data-id="element-58">
              Mega Pack Emprende Minidonas 360
            </strong>{' '}
            es mucho más que un recetario.
          </p>
          <p className="text-lg text-pink-50 mb-8 leading-relaxed relative z-10" data-id="element-59">
            Es un sistema operativo completo para tu negocio de minidonas que
            incluye recetas maestras, técnicas de decoración profesional,
            minidonas saladas y fritas, branding con celular, gestión de precios
            y estrategia de ventas.
          </p>

          <ul className="space-y-4 relative z-10" data-id="element-60">
            {['Todo organizado.', 'Todo paso a paso.', 'Todo listo para aplicar hoy.'].map((item, idx) => <li key={idx} className="flex items-center gap-3 text-xl font-bold text-white font-['Outfit',sans-serif]" data-id="element-61">
                <CheckCircle2 className="w-6 h-6 text-pink-400 flex-shrink-0" data-id="element-62" />
                {item}
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}
function TomosSection() {
  const tomos = [{
    num: 'TOMO 1',
    title: 'EL ADN DE LA MASA & SABORES ESTRELLA',
    desc: 'La masa base perfecta más 8 sabores estrella: Red Velvet, Chocolate, Limón, Oreo, Banana, Arándano, Naranja y Zanahoria.'
  }, {
    num: 'TOMO 2',
    title: 'MASTER DE GLASEADOS, COLORES Y TOPPINGS',
    desc: 'Técnicas de glaseado profesional: inmersión, drip, efecto mármol, ganache y decoración temática para cada ocasión.'
  }, {
    num: 'TOMO 3',
    title: 'BRUNCH GOURMET — MINIDONAS SALADAS',
    desc: 'Las 3 masas maestras saladas más 8 recetas completas para conquistar el mercado de brunchs y eventos.'
  }, {
    num: 'TOMO 4',
    title: 'FRITAS & RELLENAS — SABOR TRADICIONAL',
    desc: 'Masa levada para fritura perfecta más 6 rellenos clásicos: dulce de leche, Nutella, crema pastelera y más.'
  }, {
    num: 'TOMO 5',
    title: 'FOTO & BRANDING CON CELULAR',
    desc: 'El sistema completo para fotografiar tus minidonas y construir una marca irresistible usando solo tu celular.'
  }, {
    num: 'TOMO 6',
    title: 'TU NEGOCIO EN DÓLARES — GESTIÓN GLOBAL',
    desc: 'Costos, precios, canales de venta, WhatsApp Business, redes sociales y estrategia para escalar en Latinoamérica.'
  }];
  return <section className="py-20 px-4 bg-[#FFF5EB]" data-id="element-63">
      <div className="max-w-6xl mx-auto" data-id="element-64">
        <h2 className="text-3xl md:text-5xl font-black mb-16 text-center text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-65">
          EL SISTEMA COMPLETO DE 6 TOMOS
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-66">
          {tomos.map((tomo, idx) => <div key={idx} className="bg-white rounded-2xl overflow-hidden border border-amber-100 hover:border-pink-300 hover:shadow-xl transition-all duration-300 group" data-id="element-67">
              <div className="bg-pink-50 p-4 border-b border-pink-100" data-id="element-68">
                <span className="text-[#E85D75] font-bold tracking-wider text-sm font-['Outfit',sans-serif]" data-id="element-69">
                  {tomo.num}
                </span>
              </div>
              <div className="p-6" data-id="element-70">
                <h3 className="text-xl font-bold mb-4 text-[#2D1816] group-hover:text-[#E85D75] transition-colors font-['Outfit',sans-serif]" data-id="element-71">
                  {tomo.title}
                </h3>
                <p className="text-[#5C3D3A] leading-relaxed" data-id="element-72">{tomo.desc}</p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function BonosSection() {
  const bonos = [{
    title: 'EL MASTER DE TORRES Y TORTAS TEMÁTICAS',
    desc: 'El sistema completo para armar torres de minidonas y tortas temáticas que se venden solas en cumpleaños, baby showers, casamientos y eventos. El producto más fotografiable y cotizado del menú.',
    icon: <Gift className="w-10 h-10 text-pink-300" data-id="element-73" />,
    image: 'https://cdn.magicpatterns.com/uploads/sj816P7d8GnacaV4JttH7b/ChatGPT_Image_6_jul_2026,_11_18_50_p.m..png'
  }, {
    title: 'EL PLAN "PRIMERA VENTA EN 7 DÍAS"',
    desc: 'Un plan de acción día a día para vender tus primeras minidonas en menos de una semana. Incluye plantillas de mensajes para WhatsApp, textos para Instagram y guía de manejo de objeciones.',
    icon: <Zap className="w-10 h-10 text-amber-300" data-id="element-74" />,
    image: 'https://cdn.magicpatterns.com/uploads/hiombg6evQUaRAAVCE5E9p/ChatGPT_Image_6_jul_2026,_11_19_04_p.m..png'
  }, {
    title: 'MINIDONAS SANDWICH & RELLENOS EXPLOSIVOS',
    desc: 'El sistema para crear minidonas sandwich con rellenos dulces y salados que detienen el scroll y generan ventas instantáneas. El contenido más viral y compartible de todo el pack.',
    icon: <Star className="w-10 h-10 text-purple-300" data-id="element-75" />,
    image: 'https://cdn.magicpatterns.com/uploads/msvANLhAUaFWQ8TTbGkf82/ChatGPT_Image_6_jul_2026,_11_19_11_p.m..png'
  }];
  return <section className="py-20 px-4 relative overflow-hidden bg-[#4A2B29]" data-id="element-76">
      <div className="absolute inset-0 bg-gradient-to-b from-[#3E201D] to-[#4A2B29]" data-id="element-77"></div>
      <div className="max-w-5xl mx-auto relative z-10" data-id="element-78">
        <h2 className="text-3xl md:text-5xl font-black mb-16 text-center text-pink-300 font-['Outfit',sans-serif]" data-id="element-79">
          3 BONOS INCLUIDOS — GRATIS CON TU COMPRA DE HOY
        </h2>

        <div className="space-y-6 mb-12" data-id="element-80">
          {bonos.map((bono, idx) => <div key={idx} className="flex flex-col md:flex-row gap-6 md:items-center bg-white/5 p-8 rounded-2xl border border-white/10 backdrop-blur-sm hover:bg-white/10 transition-colors" data-id="element-81">
              {bono.image ? <img src={bono.image} alt={bono.title} className="flex-shrink-0 w-full md:w-56 h-auto rounded-xl shadow-lg border border-white/10 object-cover" data-id="element-82" /> : <div className="flex-shrink-0 bg-white/10 p-4 rounded-xl h-fit shadow-inner" data-id="element-83">
                  {bono.icon}
                </div>}
              <div data-id="element-84">
                <div className="text-sm font-bold text-pink-300 mb-2 tracking-wider uppercase font-['Outfit',sans-serif]" data-id="element-85">
                  BONO {idx + 1}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white font-['Outfit',sans-serif]" data-id="element-86">
                  {bono.title}
                </h3>
                <p className="text-pink-50/80 leading-relaxed text-lg" data-id="element-87">
                  {bono.desc}
                </p>
              </div>
            </div>)}
        </div>

        <div className="text-center" data-id="element-88">
          <p className="text-xl md:text-2xl font-bold text-white bg-[#E85D75]/20 py-4 px-8 rounded-full inline-block border border-[#E85D75]/30 font-['Outfit',sans-serif]" data-id="element-89">
            🎁 Los 3 bonos están incluidos GRATIS con tu compra de hoy.
          </p>
        </div>
      </div>
    </section>;
}
function PeekInsideSection() {
  const pages = [{
    src: 'https://cdn.magicpatterns.com/uploads/bkDjaQmEfN3km44BWNaY3z/Captura_de_pantalla_2026-07-07_012056.png',
    caption: 'Recetas con sabores estrella, como Red Velvet'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/6ewSREidd2zG8P5AoZZbrg/Captura_de_pantalla_2026-07-07_012157.png',
    caption: 'Glaseados y baños de chocolate paso a paso'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/r3hFBBqj7JSZ3K4WuyT5pK/Captura_de_pantalla_2026-07-07_012244.png',
    caption: 'El mercado de las minidonas saladas'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/1bnyaUS8s5D6h1UukHVbiA/Captura_de_pantalla_2026-07-07_012333.png',
    caption: 'Secretos de la fritura perfecta'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/rXqDKMabcFvwgo5RtiqoHz/Captura_de_pantalla_2026-07-07_012426.png',
    caption: 'Foto & branding: composiciones que venden'
  }, {
    src: 'https://cdn.magicpatterns.com/uploads/7eVHjC5F2HvWQpTZGGuA1r/Captura_de_pantalla_2026-07-07_012446.png',
    caption: 'Tu negocio en dólares: precios, ventas y escala'
  }];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const total = pages.length;
  const paginate = (dir: number) => {
    setDirection(dir);
    setIndex(prev => (prev + dir + total) % total);
  };
  const goTo = (i: number) => {
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };
  const variants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 60 : -60
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -60 : 60
    })
  };
  return <section className="py-20 px-4 bg-[#FFFDF9]" data-id="element-90">
      <div className="max-w-4xl mx-auto" data-id="element-91">
        <div className="text-center mb-12" data-id="element-92">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-pink-200 bg-pink-50 text-[#E85D75] text-sm font-bold tracking-wide mb-6 uppercase shadow-sm" data-id="element-93">
            <span className="w-2 h-2 rounded-full bg-[#E85D75]" data-id="element-94"></span>
            MIRÁ POR DENTRO
          </div>
          <h2 className="text-3xl md:text-5xl font-black mb-4 text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-95">
            ASÍ ES EL MATERIAL POR DENTRO
          </h2>
          <p className="text-xl text-[#5C3D3A] font-medium max-w-2xl mx-auto" data-id="element-96">
            Un vistazo real a las páginas del sistema: recetas, técnicas y guías
            explicadas paso a paso.
          </p>
        </div>

        <div className="relative max-w-sm mx-auto" data-id="element-97">
          <div className="relative overflow-hidden rounded-3xl border border-amber-100 shadow-2xl bg-white aspect-[210/297]" data-id="element-98">
            <AnimatePresence initial={false} custom={direction} mode="popLayout" data-id="element-99">
              <motion.img key={index} src={pages[index].src} alt={pages[index].caption} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.35,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-full h-full object-cover" data-id="element-100" />
            </AnimatePresence>

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 pt-16" data-id="element-101">
              <p className="text-white font-bold text-lg font-['Outfit',sans-serif]" data-id="element-102">
                Página {index + 1} de {total}
                <span className="block text-pink-200 font-medium text-base" data-id="element-103">
                  {pages[index].caption}
                </span>
              </p>
            </div>

            <button type="button" onClick={() => paginate(-1)} aria-label="Página anterior" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-[#2D1816] flex items-center justify-center shadow-lg hover:bg-white hover:scale-105 transition-all focus:outline-none focus:ring-2 focus:ring-[#E85D75]" data-id="element-104">
              <ChevronLeft className="w-6 h-6" data-id="element-105" />
            </button>
            <button type="button" onClick={() => paginate(1)} aria-label="Página siguiente" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 text-[#2D1816] flex items-center justify-center shadow-lg hover:bg-white hover:scale-105 transition-all focus:outline-none focus:ring-2 focus:ring-[#E85D75]" data-id="element-106">
              <ChevronRight className="w-6 h-6" data-id="element-107" />
            </button>
          </div>

          <div className="flex justify-center items-center gap-2.5 mt-6" data-id="element-108">
            {pages.map((_, i) => <button key={i} type="button" onClick={() => goTo(i)} aria-label={`Ir a la página ${i + 1}`} className={`h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-[#E85D75] ${i === index ? 'w-8 bg-[#E85D75]' : 'w-2.5 bg-pink-200 hover:bg-pink-300'}`} data-id="element-109" />)}
          </div>
        </div>
      </div>
    </section>;
}
function ChangesSection() {
  const changes = [{
    emoji: '⚡',
    title: 'Cero confusión, cero improvisación',
    desc: 'Abrís el tomo, seguís el paso a paso y el resultado sale. Sin buscar más, sin improvisar más.'
  }, {
    emoji: '👑',
    title: 'Autoridad y confianza',
    desc: 'Con un sistema probado en la mano, llegás a cada pedido sabiendo exactamente lo que hacés y cómo lo presentás.'
  }, {
    emoji: '📈',
    title: 'Un negocio que crece en serio',
    desc: 'Los 6 tomos te dan las herramientas para pasar de hacer minidonas para amigos a tener clientes fijos y pedidos recurrentes.'
  }, {
    emoji: '😌',
    title: 'Se acabó el pánico antes de cada pedido',
    desc: 'Dejás de improvisar decoraciones y precios. Tenés un sistema que trabaja por vos cada vez que abrís el pack.'
  }, {
    emoji: '💰',
    title: 'Ingresos reales desde casa',
    desc: 'El Tomo 6 te enseña exactamente cómo calcular tus precios, manejar tus costos y empezar a cobrar lo que realmente vale tu trabajo.'
  }];
  return <section className="py-20 px-4 bg-[#FFFDF9]" data-id="element-110">
      <div className="max-w-4xl mx-auto" data-id="element-111">
        <div className="text-center mb-16" data-id="element-112">
          <h2 className="text-3xl md:text-5xl font-black mb-4 text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-113">
            ¿QUÉ VA A CAMBIAR EN TU VIDA?
          </h2>
          <p className="text-xl text-[#E85D75] font-bold font-['Outfit',sans-serif]" data-id="element-114">
            Resultados reales desde la primera semana.
          </p>
        </div>

        <div className="space-y-4" data-id="element-115">
          {changes.map((change, idx) => <div key={idx} className="flex items-start gap-4 p-6 bg-white rounded-xl border border-amber-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-116">
              <div className="bg-pink-50 w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0" data-id="element-117">
                <span role="img" aria-label={change.title} data-id="element-118">
                  {change.emoji}
                </span>
              </div>
              <div data-id="element-119">
                <h3 className="text-xl font-bold text-[#2D1816] mb-2 font-['Outfit',sans-serif]" data-id="element-120">
                  {change.title}
                </h3>
                <p className="text-[#5C3D3A]" data-id="element-121">{change.desc}</p>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}
function OfferSection() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 1,
    minutes: 27,
    seconds: 15
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return {
          ...prev,
          seconds: prev.seconds - 1
        };
        if (prev.minutes > 0) return {
          ...prev,
          minutes: prev.minutes - 1,
          seconds: 59
        };
        if (prev.hours > 0) return {
          hours: prev.hours - 1,
          minutes: 59,
          seconds: 59
        };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  return <section className="py-24 px-4 relative bg-[#FFF5EB]" id="oferta" data-id="element-122">
      <div className="absolute inset-0 bg-gradient-to-b from-[#FFF5EB] via-pink-50/50 to-[#FFF5EB]" data-id="element-123"></div>
      <div className="max-w-2xl mx-auto relative z-10" data-id="element-124">
        {/* Título por fuera de la tarjeta, para dar contexto al bloque destacado */}
        <div className="text-center mb-8" data-id="element-125">
          <div className="inline-block bg-[#E85D75] text-white font-bold px-4 py-1.5 rounded-full text-xs mb-4 animate-pulse font-['Outfit',sans-serif] tracking-widest uppercase" data-id="element-126">
            🔥 Oferta de lanzamiento
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-[#2D1816] font-['Outfit',sans-serif] leading-tight" data-id="element-127">
            EL SISTEMA COMPLETO DE 6 TOMOS + 3 BONOS
          </h2>
        </div>

        {/* Tarjeta destacada */}
        <div className="relative" data-id="element-128">
          {/* Halo detrás de la tarjeta para que sobresalga */}
          <div className="absolute -inset-2 bg-[#E85D75]/20 rounded-[2rem] blur-2xl" data-id="element-129"></div>

          <div className="relative bg-gradient-to-b from-[#4A2B29] to-[#3E201D] rounded-3xl border border-pink-400/30 ring-1 ring-white/10 p-6 md:p-10 text-center shadow-[0_30px_70px_-20px_rgba(0,0,0,0.5)]" data-id="element-130">
            <div className="inline-block bg-amber-400 text-[#2D1816] font-black px-4 py-1.5 rounded-full text-sm mb-6 font-['Outfit',sans-serif]" data-id="element-131">
              82% de descuento
            </div>

            <div className="relative -mx-2 sm:-mx-6 md:-mx-12 mb-8" data-id="element-132">
              <img src="https://cdn.magicpatterns.com/uploads/7BWHgWK9Xb1uuBT1NMp52c/ChatGPT_Image_7_jul_2026,_06_39_02_p.m..png" alt="Los 6 tomos del Mega Pack Emprende Minidonas 360, un sistema completo" className="w-full h-auto block" data-id="element-133" />
            </div>

            <div className="mb-8" data-id="element-134">
              <p className="text-pink-200/70 text-lg line-through mb-1 font-medium" data-id="element-135">
                Precio regular: USD 55
              </p>
              <p className="text-[#E85D75] font-black tracking-widest text-sm mb-1 font-['Outfit',sans-serif] uppercase" data-id="element-136">
                Solo hoy:
              </p>
              <p className="text-5xl md:text-7xl font-black text-white mb-2 font-['Outfit',sans-serif]" data-id="element-137">
                <span className="text-pink-300 text-[40px] font-[700]" data-id="element-138">
                  USD
                </span>{' '}
                9.97
              </p>
              <p className="text-pink-100/70 font-medium text-sm" data-id="element-139">
                Pagá de forma segura en tu moneda local.
              </p>
            </div>

            <div className="bg-black/20 border border-[#E85D75]/30 rounded-2xl p-5 mb-8" data-id="element-140">
              <p className="text-pink-200 font-bold mb-4 flex items-center justify-center gap-2 text-sm font-['Outfit',sans-serif]" data-id="element-141">
                <Clock className="w-4 h-4" data-id="element-142" /> ¡Última oportunidad! El precio
                sube cuando termine la cuenta regresiva.
              </p>
              <div className="flex items-stretch justify-center gap-3" data-id="element-143">
                {[{
                value: timeLeft.hours,
                label: 'Horas'
              }, {
                value: timeLeft.minutes,
                label: 'Minutos'
              }, {
                value: timeLeft.seconds,
                label: 'Segundos'
              }].map((unit, i) => <div key={i} className="flex-1 max-w-[100px] bg-[#3E201D] rounded-xl border border-white/10 py-3" data-id="element-144">
                    <div className="text-3xl md:text-4xl font-black text-white font-['Outfit',sans-serif] tabular-nums" data-id="element-145">
                      {String(unit.value).padStart(2, '0')}
                    </div>
                    <div className="text-[10px] md:text-xs text-pink-200/70 font-semibold tracking-widest uppercase mt-1" data-id="element-146">
                      {unit.label}
                    </div>
                  </div>)}
              </div>
            </div>

            <CTAButton text="✅ SÍ, QUIERO EL PACK COMPLETO (USD 9.97)" data-id="element-147" />

            <div className="mt-8 flex flex-col items-center gap-2 text-sm text-pink-100/80 font-medium" data-id="element-148">
              <div className="flex items-center gap-1 text-amber-400" data-id="element-149">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-150" />)}
              </div>
              <p data-id="element-151">4.9/5 — Reseñas verificadas</p>
              <p className="flex items-center gap-1" data-id="element-152">
                <ShieldCheck className="w-4 h-4 text-green-400" data-id="element-153" /> Garantía de
                devolución de 7 días: riesgo cero.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function GuaranteeSection() {
  return <section className="py-20 px-4 bg-[#FFFDF9]" data-id="element-154">
      <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-10 bg-[#FFF5EB] p-8 md:p-12 rounded-3xl border border-amber-200 shadow-sm" data-id="element-155">
        <div className="flex-shrink-0" data-id="element-156">
          <div className="w-32 h-32 bg-gradient-to-br from-amber-300 to-amber-500 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(245,158,11,0.3)]" data-id="element-157">
            <ShieldCheck className="w-16 h-16 text-white" data-id="element-158" />
          </div>
        </div>
        <div data-id="element-159">
          <h2 className="text-3xl font-black text-[#2D1816] mb-2 font-['Outfit',sans-serif]" data-id="element-160">
            TU INVERSIÓN ESTÁ 100% PROTEGIDA
          </h2>
          <h3 className="text-xl font-bold text-[#E85D75] mb-6 font-['Outfit',sans-serif]" data-id="element-161">
            GARANTÍA TOTAL DE 7 DÍAS
          </h3>
          <div className="space-y-4 text-[#5C3D3A] font-medium" data-id="element-162">
            <p data-id="element-163">Tu dinero está completamente protegido.</p>
            <p data-id="element-164">
              Tenés 7 días completos para explorar los 6 tomos, probar las
              recetas, usar los bonos y evaluar el sistema completo.
            </p>
            <p data-id="element-165">
              Si por cualquier motivo no quedás 100% satisfecha, simplemente
              escribinos a atencionrecursohub@gmail.com y te devolvemos el 100%
              de tu compra.
            </p>
            <p className="font-bold text-[#2D1816]" data-id="element-166">
              Cero riesgo para vos. Compromiso total de nuestra parte.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function TestimonialsSection() {
  return <section className="py-20 px-4 bg-[#FFF5EB]" data-id="element-167">
      <div className="max-w-5xl mx-auto" data-id="element-168">
        <h2 className="text-3xl md:text-5xl font-black mb-16 text-center text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-169">
          LO QUE DICEN OTRAS EMPRENDEDORAS
        </h2>

        <div className="grid md:grid-cols-2 gap-8" data-id="element-170">
          <div className="bg-white p-8 rounded-2xl border border-amber-100 shadow-sm relative" data-id="element-171">
            <div className="flex items-center gap-1 text-amber-400 mb-4" data-id="element-172">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-173" />)}
            </div>
            <p className="text-[#5C3D3A] text-lg italic mb-6 leading-relaxed" data-id="element-174">
              "Mi mayor problema era no saber por dónde empezar. El Mega Pack me
              dio un sistema claro, paso a paso. En menos de dos semanas ya
              tenía mis primeros pedidos confirmados."
            </p>
            <div className="flex items-center gap-4" data-id="element-175">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center text-xl font-bold text-[#E85D75] font-['Outfit',sans-serif]" data-id="element-176">
                M
              </div>
              <div data-id="element-177">
                <p className="font-bold text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-178">
                  María G.
                </p>
                <p className="text-sm text-[#5C3D3A]" data-id="element-179">
                  Emprendedora desde casa, Buenos Aires
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-amber-100 shadow-sm relative" data-id="element-180">
            <div className="flex items-center gap-1 text-amber-400 mb-4" data-id="element-181">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" data-id="element-182" />)}
            </div>
            <p className="text-[#5C3D3A] text-lg italic mb-6 leading-relaxed" data-id="element-183">
              "Las minidonas saladas del Tomo 3 me abrieron un mercado que no
              sabía que existía. Ahora hago catering de brunchs y cobro el
              triple de lo que cobraba antes."
            </p>
            <div className="flex items-center gap-4" data-id="element-184">
              <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center text-xl font-bold text-[#E85D75] font-['Outfit',sans-serif]" data-id="element-185">
                V
              </div>
              <div data-id="element-186">
                <p className="font-bold text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-187">
                  Valentina R.
                </p>
                <p className="text-sm text-[#5C3D3A]" data-id="element-188">Pastelera, Bogotá</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: '¿ES UN PRODUCTO FÍSICO O DIGITAL?',
    a: 'El Mega Pack Emprende Minidonas 360 es un producto 100% digital que se entrega de forma instantánea en formato PDF. Podés acceder desde tu celular, tablet o computadora en cualquier momento.'
  }, {
    q: '¿NECESITO EXPERIENCIA PREVIA EN PASTELERÍA?',
    a: 'No. El sistema está diseñado desde cero para que cualquier persona pueda seguirlo sin experiencia previa. Cada tomo va paso a paso con explicaciones claras y técnicas accesibles.'
  }, {
    q: '¿FUNCIONA PARA TODOS LOS PAÍSES DE LATINOAMÉRICA?',
    a: 'Sí. El sistema usa nombres neutros para los ingredientes y una guía de equivalencias para Argentina, México, Colombia, Chile, Perú y toda Latinoamérica.'
  }, {
    q: '¿LOS BONOS ESTÁN INCLUIDOS?',
    a: 'Sí. Los 3 bonos están incluidos de forma completamente gratuita con tu compra de hoy. No hay costos adicionales ni sorpresas.'
  }, {
    q: '¿PUEDO IMPRIMIR LOS MATERIALES?',
    a: 'Sí. Todos los tomos y bonos están en formato PDF de alta calidad, listos para imprimir o usar en pantalla según prefieras.'
  }];
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  return <section className="py-20 px-4 bg-[#FFFDF9]" data-id="element-189">
      <div className="max-w-3xl mx-auto" data-id="element-190">
        <h2 className="text-3xl md:text-5xl font-black mb-12 text-center text-[#2D1816] font-['Outfit',sans-serif]" data-id="element-191">
          PREGUNTAS FRECUENTES
        </h2>

        <div className="space-y-4" data-id="element-192">
          {faqs.map((faq, idx) => <div key={idx} className="bg-white rounded-xl border border-amber-100 overflow-hidden shadow-sm hover:border-pink-200 transition-colors" data-id="element-193">
              <button className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenIdx(openIdx === idx ? null : idx)} data-id="element-194">
                <span className="font-bold text-[#2D1816] pr-4 font-['Outfit',sans-serif]" data-id="element-195">
                  {faq.q}
                </span>
                {openIdx === idx ? <ChevronUp className="w-5 h-5 text-[#E85D75] flex-shrink-0" data-id="element-196" /> : <ChevronDown className="w-5 h-5 text-amber-400 flex-shrink-0" data-id="element-197" />}
              </button>
              {openIdx === idx && <div className="px-6 pb-5 text-[#5C3D3A] leading-relaxed font-medium" data-id="element-198">
                  {faq.a}
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}
function FinalCloseSection() {
  return <section className="py-24 px-4 relative overflow-hidden bg-[#4A2B29]" data-id="element-199">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-[#3E201D] via-[#4A2B29] to-[#4A2B29]" data-id="element-200"></div>
      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-201">
        <h2 className="text-4xl md:text-6xl font-black text-white mb-8 font-['Outfit',sans-serif]" data-id="element-202">
          ÚLTIMA OPORTUNIDAD
        </h2>

        <div className="text-xl text-pink-50/90 space-y-6 mb-12 max-w-2xl mx-auto font-medium" data-id="element-203">
          <p data-id="element-204">Tu negocio de minidonas merece la mejor versión tuya.</p>
          <p data-id="element-205">
            No la versión que improvisa recetas buscando en internet a las 11 de
            la noche.
          </p>
          <p data-id="element-206">
            Dejá de buscar recetas sueltas que no funcionan. Dejá de improvisar
            precios y presentaciones.
          </p>
          <p className="font-bold text-white" data-id="element-207">
            Tomá el control de tu emprendimiento con un sistema completo
            diseñado para que empieces a vender desde el primer día.
          </p>
        </div>

        <div className="bg-[#3E201D]/80 backdrop-blur-sm p-8 md:p-10 rounded-3xl border border-[#5C3D3A] inline-block w-full max-w-2xl shadow-2xl" data-id="element-208">
          <h3 className="text-2xl md:text-3xl font-black text-pink-300 mb-3 font-['Outfit',sans-serif]" data-id="element-209">
            MEGA PACK EMPRENDE MINIDONAS 360
          </h3>
          <p className="text-white mb-8 font-medium text-lg" data-id="element-210">
            6 Tomos completos + 3 Bonos premium
          </p>

          <div className="flex items-center justify-center gap-4 mb-8" data-id="element-211">
            <span className="text-pink-200/60 line-through text-xl font-medium" data-id="element-212">
              USD 55
            </span>
            <span className="text-5xl font-black text-white font-['Outfit',sans-serif]" data-id="element-213">
              USD 9.97
            </span>
          </div>

          <CTAButton text="✅ SÍ, QUIERO EMPRENDER CON MINIDONAS AHORA" data-id="element-214" />

          <div className="mt-8 flex flex-col items-center gap-2 text-sm text-pink-100/80 font-medium" data-id="element-215">
            <p data-id="element-216">Acceso instantáneo: se entrega por correo electrónico.</p>
            <div className="flex items-center gap-1 text-amber-400" data-id="element-217">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-218" />)}
            </div>
            <p data-id="element-219">4.9/5 — Reseñas verificadas</p>
            <p className="flex items-center gap-1" data-id="element-220">
              <ShieldCheck className="w-4 h-4 text-green-400" data-id="element-221" /> Garantía de
              devolución de 7 días: riesgo cero.
            </p>
          </div>
        </div>
      </div>
    </section>;
}
function Footer() {
  return <footer className="bg-[#2D1816] py-12 px-4 border-t border-[#3E201D]" data-id="element-222">
      <div className="max-w-5xl mx-auto text-center text-[#A88B88] text-sm space-y-6 font-medium" data-id="element-223">
        <p data-id="element-224">
          © 2025 RecursoHub — Todos los derechos reservados.
          <br data-id="element-225" />
          www.recursohub.com | atencionrecursohub@gmail.com
        </p>

        <p className="max-w-3xl mx-auto text-xs leading-relaxed opacity-80" data-id="element-226">
          El Mega Pack Emprende Minidonas 360 es un recurso educativo digital
          diseñado para emprendedoras que desean iniciar o profesionalizar su
          negocio de minidonas. Los resultados individuales pueden variar según
          el esfuerzo, dedicación y contexto de cada persona.
        </p>

        <div className="flex flex-wrap justify-center gap-4 text-xs" data-id="element-227">
          <a href="#" className="hover:text-pink-300 transition-colors" data-id="element-228">
            Términos y Condiciones
          </a>
          <span className="opacity-50" data-id="element-229">|</span>
          <a href="#" className="hover:text-pink-300 transition-colors" data-id="element-230">
            Política de Privacidad
          </a>
          <span className="opacity-50" data-id="element-231">|</span>
          <a href="#" className="hover:text-pink-300 transition-colors" data-id="element-232">
            Contacto
          </a>
        </div>
      </div>
    </footer>;
}